import { useMemo, useCallback } from "react"
import { Link, useSearchParams } from "react-router-dom"
import { Box, useTheme, CircularProgress } from "@mui/material"
import { get, orderBy, take } from "lodash"
import { AppNavigation } from "containers/AppNavigation"
import { PageCard, PageCardContent } from "components/card"
import { NavigationControl } from "components/kioskHorizontal/navigation"

import { MovieCard } from "extensions/movie-show-time/components/MovieCard"
import { CinemaToggleButton } from "extensions/movie-show-time/components/CinemaToggleButton"

import { useExtension } from "hooks/extension/useExtension"
import { useMovieShowTimePage } from "extensions/movie-show-time/hooks/useMovieShowTimePage"
import { useMovieShowTime } from "extensions/movie-show-time/hooks/useMovieShowTime"

import { EXTENSION_NAME } from "extensions/movie-show-time/constants"

export const MovieShowTimeKioskHPage = ({ Layout = Box }) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { extensionConfig } = useExtension()
  const cinemaConfig = useMemo(
    () => get(extensionConfig, EXTENSION_NAME, null),
    [extensionConfig]
  )
  const { cinemas, defaultCinema } = useMovieShowTimePage(cinemaConfig)
  const viewingCinema = searchParams.get("cinema") || defaultCinema?.id
  const { showtime, isLoading } = useMovieShowTime(viewingCinema, {
    enabled: !!defaultCinema?.id,
  })

  const orderedShowTime = useMemo(
    () => orderBy(showtime, ["image"]),
    [showtime]
  )

  const fallbackPoster = useMemo(() => {
    const cinemas = get(cinemaConfig, "options.cinemas", [])
    const matchedCinemaConfig = cinemas.find(({ id }) => id === viewingCinema)
    return get(matchedCinemaConfig, "fallback_poster", null)
  }, [viewingCinema, cinemaConfig])

  const theme = useTheme()

  const bannerStyleOverrides =
    theme.components[`AppBanner`]?.styleOverrides || {}
  const sideBarStyleOverrides =
    theme.components[`AppSidebar`]?.styleOverrides || {}

  const { root: bannerRoot } = bannerStyleOverrides
  const { wrapper, body, footer } = sideBarStyleOverrides
  const styleOverrides =
    theme.components[`MovieShowTimesPage`]?.styleOverrides || {}
  const { root, pageCardContent, gridContainer, cinemasFilterContainer } =
    styleOverrides
  const loadingstyleOverrides =
    theme.components[`MovieLoadingScreen`]?.styleOverrides || {}
  const { container } = loadingstyleOverrides
  const handleCinemaChange = useCallback(
    (event, newCinema) => {
      if (newCinema !== null) {
        setSearchParams({ cinema: newCinema }, { replace: true })
      }
    },
    [setSearchParams]
  )
  return (
    <Layout
      aside={
        <>
          <PageCard>
            <Box sx={wrapper}>
              <Box sx={bannerRoot}></Box>
              <Box sx={body}>
                <AppNavigation />
              </Box>
              <Box sx={footer}>
                <NavigationControl BackButtonProps={{ hidden: true }} />
              </Box>
            </Box>
          </PageCard>
        </>
      }
    >
      <PageCard sx={root}>
        <PageCardContent sx={pageCardContent}>
          <Box sx={cinemasFilterContainer}>
            <CinemaToggleButton
              value={viewingCinema}
              onChange={handleCinemaChange}
              cinemas={cinemas.map(({ id, name }) => ({
                value: id,
                label: name,
              }))}
            />
          </Box>
          {isLoading ? (
            <Box sx={container}>
              <CircularProgress color="primary" />
            </Box>
          ) : (
            <Box sx={gridContainer}>
              {orderedShowTime.map(({ image, title, genre, duration, id }) => (
                <MovieCard
                  key={id}
                  component={Link}
                  to={`/movie/${id}/cinema/${viewingCinema}`}
                  poster={image || fallbackPoster}
                  title={title}
                  genre={take(genre, 2).join(" | ")}
                  duration={duration && `${duration} MIN`}
                />
              ))}
            </Box>
          )}
        </PageCardContent>
      </PageCard>
    </Layout>
  )
}
